import type { Terminology } from '~/_api/models/terminology'
import type { TerminologyOverrides, TerminologyTermValue } from '~/_api/types/interfaces/terminology'

/**
 * Load / save pipeline for the Terminology preferences page. Unlike the
 * Location-backed pages (`useLocationSettings`), the API only accepts the
 * terms that actually changed rather than the full dictionary, so saves are
 * diffed against a snapshot of `{ singular, plural }` per term key before
 * being sent.
 */
export function useTerminologySettings() {
  const { $api, $auth, $notification, $i18n } = useNuxtApp()

  const terminology = ref<Terminology | null>(null)
  const originalTerms = ref<Record<string, TerminologyTermValue>>({})

  const captureOriginalTerms = () => {
    originalTerms.value = Object.fromEntries(
      Object.entries(terminology.value?.terms ?? {}).map(([key, term]) => [
        key,
        { singular: term.singular, plural: term.plural },
      ]),
    )
  }

  const fetchTerminology = async () => {
    try {
      const response = await ($api as any).terminology.terminology()
      if (response?.data) {
        terminology.value = response.data
        captureOriginalTerms()
      }
    } catch {
      ($notification as any).error()
    }
  }

  const changedTerms = (): TerminologyOverrides => {
    const overrides: TerminologyOverrides = {}
    for (const [key, term] of Object.entries(terminology.value?.terms ?? {})) {
      const original = originalTerms.value[key]
      if (!original || original.singular !== term.singular || original.plural !== term.plural) {
        overrides[key] = { singular: term.singular, plural: term.plural }
      }
    }
    return overrides
  }

  const unsavedChanges = computed(() => Object.keys(changedTerms()).length > 0)

  const save = async () => {
    const overrides = changedTerms()
    if (Object.keys(overrides).length === 0) return

    try {
      const response = await ($api as any).terminology.saveTerminology(overrides)
      if (response?.data) {
        terminology.value = response.data
        captureOriginalTerms()
        ;($notification as any).toast({
          title: ($i18n as any).t('general.saved'),
          text: ($i18n as any).t('manage.savedSuccessInfo'),
          variant: 'success',
        })
        ;($auth as any).syncInfo()
      }
    } catch {
      ($notification as any).error()
    }
  }

  const cancel = () => fetchTerminology()

  const resetTerm = async (key: string) => {
    const term = terminology.value?.terms[key]
    if (!term) return

    if (!term.custom) {
      term.singular = term.default.singular
      term.plural = term.default.plural
      return
    }

    try {
      const response = await ($api as any).terminology.saveTerminology({
        [key]: { singular: '', plural: '' },
      })
      const updated = response?.data?.terms?.[key]
      if (updated && terminology.value) {
        terminology.value.terms[key] = updated
        originalTerms.value[key] = { singular: updated.singular, plural: updated.plural }
        ;($auth as any).syncInfo()
      }
    } catch {
      ($notification as any).error()
    }
  }

  return {
    terminology,
    unsavedChanges,
    fetchTerminology,
    save,
    cancel,
    resetTerm,
  }
}
